import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Leaf, MagnifyingGlass, MapPin, CalendarDots, Camera, PawPrint, ArrowLeft, Tag, CaretLeft, CaretRight
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { API } from "@/config/constants";

function ProtectedImage({ src, alt, className }) {
  return (
    <div className="relative select-none" style={{ WebkitUserSelect: "none", userSelect: "none" }}>
      <img
        src={src}
        alt={alt}
        className={className}
        draggable={false}
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
        style={{ pointerEvents: "none", WebkitUserDrag: "none" }}
      />
      {/* Transparent overlay prevents right-click save */}
      <div
        className="absolute inset-0 z-10"
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
      />
    </div>
  );
}

function PublicEntryCard({ entry, onClick }) {
  const mainPhoto = entry.photos?.[0];
  const photoSrc = mainPhoto ? `${API}/photos/${mainPhoto.storage_path}` : null;
  return (
    <div
      onClick={onClick}
      className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-default)] overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
      data-testid={`public-entry-${entry.id}`}
    >
      {photoSrc && (
        <div className="aspect-[4/3] overflow-hidden">
          <ProtectedImage
            src={photoSrc}
            alt={entry.title}
            className="w-full h-full object-cover bg-[var(--bg-secondary)]"
          />
        </div>
      )}
      <div className="p-4">
        <h3 className="font-['Cormorant_Garamond'] text-lg font-semibold text-[var(--text-primary)] line-clamp-1">{entry.title}</h3>
        {entry.content && (
          <p className="text-sm text-[var(--text-secondary)] font-['Manrope'] mt-1 line-clamp-2">{entry.content}</p>
        )}
        <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-[var(--text-tertiary)] font-['Manrope']">
          {entry.location && (
            <span className="flex items-center gap-1"><MapPin size={14} /> {entry.location}</span>
          )}
          {entry.created_at && (
            <span className="flex items-center gap-1"><CalendarDots size={14} /> {new Date(entry.created_at).toLocaleDateString()}</span>
          )}
        </div>
        {entry.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {entry.tags.slice(0, 3).map(t => (
              <Badge key={t} className="bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] text-xs">{t}</Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PublicEntryDetail({ entry, onBack }) {
  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={onBack} className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] mb-6 font-['Manrope'] text-sm" data-testid="back-to-gallery">
        <ArrowLeft size={18} /> Back to Gallery
      </button>
      <h1 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[var(--text-primary)] mb-2">{entry.title}</h1>
      <div className="flex flex-wrap items-center gap-4 text-sm text-[var(--text-secondary)] font-['Manrope'] mb-6">
        {entry.location && <span className="flex items-center gap-1"><MapPin size={16} /> {entry.location}</span>}
        {entry.created_at && <span className="flex items-center gap-1"><CalendarDots size={16} /> {new Date(entry.created_at).toLocaleDateString()}</span>}
      </div>
      {entry.photos?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          {entry.photos.map((p, i) => (
            <div key={i} className="rounded-xl overflow-hidden border border-[var(--border-default)]">
              <ProtectedImage
                src={`${API}/photos/${p.storage_path}`}
                alt={p.caption || entry.title}
                className="w-full object-contain bg-[var(--bg-secondary)]"
              />
            </div>
          ))}
        </div>
      )}
      {entry.content && (
        <div className="prose max-w-none mb-6">
          <p className="text-[var(--text-primary)] font-['Manrope'] leading-relaxed whitespace-pre-wrap">{entry.content}</p>
        </div>
      )}
      {entry.species_identified?.length > 0 && (
        <div className="mb-6">
          <h3 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
            <PawPrint size={20} weight="duotone" /> Species Identified
          </h3>
          <div className="space-y-2">
            {entry.species_identified.map((s, i) => (
              <div key={i} className="bg-[var(--bg-secondary)] rounded-lg p-3">
                <p className="font-semibold text-[var(--text-primary)] font-['Manrope']">{s.common_name || s.scientific_name}</p>
                {s.scientific_name && s.common_name && (
                  <p className="text-sm italic text-[var(--text-secondary)]">{s.scientific_name}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {entry.tags?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {entry.tags.map(t => (
            <Badge key={t} className="bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]">
              <Tag size={12} className="mr-1" /> {t}
            </Badge>
          ))}
        </div>
      )}
      {entry.ai_insights && (
        <div className="mt-6 bg-[var(--bg-secondary)] rounded-xl p-4 border border-[var(--border-default)]">
          <h3 className="font-['Cormorant_Garamond'] text-lg font-semibold text-[var(--text-primary)] mb-2">AI Insights</h3>
          <p className="text-sm text-[var(--text-secondary)] font-['Manrope'] whitespace-pre-wrap">{entry.ai_insights}</p>
        </div>
      )}
    </div>
  );
}

export default function PublicGalleryPage() {
  const navigate = useNavigate();
  const { entryId } = useParams();
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 9;

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [entriesRes, statsRes] = await Promise.all([
          axios.get(`${API}/public/entries?limit=200`),
          axios.get(`${API}/public/stats`)
        ]);
        setEntries(entriesRes.data);
        setStats(statsRes.data);
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetchAll();
  }, []);

  useEffect(() => {
    if (entryId && entries.length > 0) {
      const found = entries.find(e => e.id === entryId);
      if (found) setSelectedEntry(found);
    }
  }, [entryId, entries]);

  const filtered = search
    ? entries.filter(e =>
        e.title?.toLowerCase().includes(search.toLowerCase()) ||
        e.content?.toLowerCase().includes(search.toLowerCase()) ||
        e.location?.toLowerCase().includes(search.toLowerCase()) ||
        e.tags?.some(t => t.toLowerCase().includes(search.toLowerCase()))
      )
    : entries;

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginatedEntries = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Leaf size={40} weight="duotone" className="text-[var(--brand-primary)] mx-auto mb-3 animate-pulse" />
          <p className="text-[var(--text-secondary)] font-['Manrope']">Loading gallery...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      onContextMenu={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S' || e.key === 'p' || e.key === 'P')) e.preventDefault();
        if (e.key === 'PrintScreen') e.preventDefault();
      }}
      style={{ WebkitUserSelect: "none", userSelect: "none" }}
    >
      {/* Header */}
      <header className="border-b border-[var(--border-default)] bg-[var(--bg-card)]">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:py-5">
          <div className="flex items-center justify-between gap-2 mb-2 sm:mb-3">
            <div className="flex items-center gap-2">
              <Leaf size={24} weight="duotone" className="text-[var(--brand-primary)]" />
              <h1 className="font-['Cormorant_Garamond'] text-lg sm:text-2xl font-bold text-[var(--text-primary)]">Nature Journal</h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/login")}
                className="text-xs sm:text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] font-['Manrope'] font-medium"
                data-testid="public-login-btn"
              >
                Sign In
              </button>
              <button
                onClick={() => navigate("/welcome")}
                className="bg-[#C25A3C] hover:bg-[#a84830] text-white text-xs sm:text-sm px-3 py-1.5 rounded-lg font-['Manrope'] font-semibold transition-colors"
                data-testid="public-get-started-btn"
              >
                Get Your Own
              </button>
            </div>
          </div>
          {stats && (
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-[var(--text-secondary)] font-['Manrope']">
              <span className="flex items-center gap-1"><Leaf size={14} /> {stats.total_entries} Entries</span>
              <span className="flex items-center gap-1"><PawPrint size={14} /> {stats.total_species} Species</span>
              <span className="flex items-center gap-1"><Camera size={14} /> {stats.total_photos} Photos</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8" data-testid="public-gallery">
        {selectedEntry ? (
          <PublicEntryDetail entry={selectedEntry} onBack={() => { setSelectedEntry(null); navigate("/public"); }} />
        ) : (
          <>
            {/* Search */}
            <div className="relative max-w-md mb-8">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
              <Input
                placeholder="Search entries..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-[var(--bg-card)] border-[var(--border-default)]"
                data-testid="public-search"
              />
            </div>

            {/* Grid */}
            {filtered.length === 0 ? (
              <p className="text-center text-[var(--text-secondary)] font-['Manrope'] py-12">No entries found.</p>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {paginatedEntries.map(entry => (
                    <PublicEntryCard
                      key={entry.id}
                      entry={entry}
                      onClick={() => { setSelectedEntry(entry); navigate(`/public/${entry.id}`); }}
                    />
                  ))}
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-3 mt-6">
                    <button
                      onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo(0, 0); }}
                      disabled={page === 1}
                      className="bg-[#55624F] text-white px-4 py-2 rounded-lg text-sm font-['Manrope'] disabled:opacity-30 hover:bg-[#424D3D] transition-colors flex items-center gap-1"
                    >
                      <CaretLeft size={16} />Previous
                    </button>
                    <span className="text-[var(--text-secondary)] text-sm font-['Manrope']">{page} / {totalPages}</span>
                    <button
                      onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo(0, 0); }}
                      disabled={page >= totalPages}
                      className="bg-[#55624F] text-white px-4 py-2 rounded-lg text-sm font-['Manrope'] disabled:opacity-30 hover:bg-[#424D3D] transition-colors flex items-center gap-1"
                    >
                      Next<CaretRight size={16} />
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[var(--border-default)] py-8 text-center space-y-3">
        <div className="flex items-center justify-center gap-2 text-[var(--text-secondary)] text-sm font-['Manrope']">
          <Leaf size={16} weight="duotone" className="text-[var(--brand-primary)]" />
          Nature Journal &copy; {new Date().getFullYear()}
        </div>
        <a
          href="https://conniekelts.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors font-['Manrope']"
          data-testid="return-to-site-link"
        >
          <img src={require("../assets/ck-design-avatar.png")} alt="" className="w-8 h-8 rounded-full object-cover border border-white/30 bg-white" />
          &larr; Return to CK Design
        </a>
      </footer>
    </div>
  );
}
